import { useState, useEffect } from 'react'
import { Shield, Eye, Check, X, Clock, FileText, Building, AlertTriangle, Users, RefreshCw } from 'lucide-react'
import { toast } from 'react-toastify'
import apiService from '../../services/api'

interface GovVendor {
  id: string
  email: string
  firstName: string
  lastName: string
  companyName: string
  locationCity: string
  locationState: string
  phone: string
  avatar: string | null
  createdAt: string
  updatedAt: string
  govStatus: 'none' | 'pending' | 'approved' | 'rejected'
  isGovCertified: boolean
  cageCode: string | null
  dunsNumber: string | null
  gsaScheduleId: string | null
  govCertifications: string[]
  approvedAgencies: string[]
  govDocsUrls: string[]
}

interface VendorDetail extends GovVendor {
  street: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  country: string | null
  businessLicense: string | null
  certifications: string[]
  specialties: string[]
  vendor_projects: Array<{
    id: string
    title: string
    description: string
    status: string
    projectType: string | null
    systemSizeKw: number | null
    location: string | null
    completedAt: string | null
    createdAt: string
  }>
}

interface PaginationInfo {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export default function GovVendorsPage() {
  const [vendors, setVendors] = useState<GovVendor[]>([])
  const [selectedVendor, setSelectedVendor] = useState<VendorDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [approvalNotes, setApprovalNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejectionNotes, setRejectionNotes] = useState('')
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  })

  useEffect(() => {
    fetchVendors()
  }, [statusFilter])

  const fetchVendors = async () => {
    debugger;
    setLoading(true)
    try {
      const response = await apiService.get(
        `/api/admin/gov-vendors?status=${statusFilter}&limit=${pagination.limit}&offset=0`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      ) as any

      if (response?.success) {
        setVendors(response.data || [])
        setPagination(response.pagination || pagination)
      } else {
        toast.error('Failed to fetch government vendors')
      }
    } catch (error) {
      console.error('Error fetching government vendors:', error)
      toast.error('Failed to fetch government vendors..')
    } finally {
      setLoading(false)
    }
  }

  const fetchVendorDetail = async (vendorId: string) => {
    setDetailLoading(true)
    try {
      const response = await apiService.get(`/api/admin/gov-vendors/${vendorId}`) as any

      if (response?.success) {
        setSelectedVendor(response.data)
        setShowDetailModal(true)
      } else {
        toast.error('Failed to fetch vendor details')
      }
    } catch (error) {
      console.error('Error fetching vendor details:', error)
      toast.error('Failed to fetch vendor details')
    } finally {
      setDetailLoading(false)
    }
  }

  const handleApprove = async (vendorId: string) => {
    setActionLoading(vendorId)
    try {
      const response = await apiService.post(`/api/admin/gov-vendors/${vendorId}/approve`, {
        notes: approvalNotes
      }) as any

      if (response?.success) {
        toast.success('Government certification approved successfully')
        fetchVendors()
        setShowApprovalModal(false)
        setShowDetailModal(false)
        setApprovalNotes('')
      } else {
        toast.error(response?.message || 'Failed to approve certification')
      }
    } catch (error: any) {
      console.error('Error approving certification:', error)
      toast.error(error.response?.data?.message || 'Failed to approve certification')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async (vendorId: string) => {
    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required')
      return
    }

    setActionLoading(vendorId)
    try {
      const response = await apiService.post(`/api/admin/gov-vendors/${vendorId}/reject`, {
        reason: rejectionReason,
        notes: rejectionNotes
      }) as any

      if (response?.success) {
        toast.success('Government certification rejected')
        fetchVendors()
        setShowRejectionModal(false)
        setShowDetailModal(false)
        setRejectionReason('')
        setRejectionNotes('')
      } else {
        toast.error(response?.message || 'Failed to reject certification')
      }
    } catch (error: any) {
      console.error('Error rejecting certification:', error)
      toast.error(error.response?.data?.message || 'Failed to reject certification')
    } finally {
      setActionLoading(null)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Review' },
      approved: { color: 'bg-green-100 text-green-800', icon: Check, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: X, label: 'Rejected' },
      none: { color: 'bg-gray-100 text-gray-800', icon: AlertTriangle, label: 'Not Applied' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.none
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const VendorRow = ({ vendor }: { vendor: GovVendor }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
            {vendor.avatar ? (
              <img src={vendor.avatar} alt={vendor.companyName} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <Building className="w-5 h-5 text-gray-500" />
            )}
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {vendor.companyName}
            </div>
            <div className="text-sm text-gray-500">
              {vendor.firstName} {vendor.lastName}
            </div>
            <div className="text-xs text-gray-400">
              {vendor.email}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {vendor.locationCity}, {vendor.locationState}
        </div>
        {vendor.cageCode && (
          <div className="text-xs text-blue-600">
            CAGE: {vendor.cageCode}
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {getStatusBadge(vendor.govStatus)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(vendor.updatedAt)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex space-x-2">
          <button
            onClick={() => fetchVendorDetail(vendor.id)}
            disabled={detailLoading}
            className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>
          {vendor.govStatus === 'pending' && (
            <>
              <button
                onClick={() => {
                  setSelectedVendor(vendor as VendorDetail)
                  setShowApprovalModal(true)
                }}
                disabled={actionLoading === vendor.id}
                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                title="Approve"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setSelectedVendor(vendor as VendorDetail)
                  setShowRejectionModal(true)
                }}
                disabled={actionLoading === vendor.id}
                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                title="Reject"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  )

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="h-8 w-8 text-emerald-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Government Vendor Management</h1>
              <p className="text-gray-600">Review and approve government certification applications</p>
            </div>
          </div>
          <button
            onClick={fetchVendors}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh...</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {[
          { status: 'pending', label: 'Pending Review', color: 'bg-yellow-50 border-yellow-200' },
          { status: 'approved', label: 'Approved', color: 'bg-green-50 border-green-200' },
          { status: 'rejected', label: 'Rejected', color: 'bg-red-50 border-red-200' },
          { status: 'all', label: 'Total Applications', color: 'bg-blue-50 border-blue-200' }
        ].map((stat) => (
          <div key={stat.status} className={`p-6 rounded-lg border ${stat.color}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stat.status === statusFilter ? pagination.total : '-'}
                </p>
              </div>
              <Users className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All Applications</option>
          </select>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Updated
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mb-2" />
                    <span className="text-gray-500">Loading government vendors...</span>
                  </div>
                </td>
              </tr>
            ) : vendors.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <Shield className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-gray-500">No government vendor applications found</span>
                  </div>
                </td>
              </tr>
            ) : (
              vendors.map((vendor) => (
                <VendorRow key={vendor.id} vendor={vendor} />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedVendor && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Government Vendor Details: {selectedVendor.companyName}
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Vendor Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Company Information</h4>
                  <div className="space-y-2">
                    <p><strong>Company:</strong> {selectedVendor.companyName}</p>
                    <p><strong>Contact:</strong> {selectedVendor.firstName} {selectedVendor.lastName}</p>
                    <p><strong>Email:</strong> {selectedVendor.email}</p>
                    <p><strong>Phone:</strong> {selectedVendor.phone || 'Not provided'}</p>
                    <p><strong>Location:</strong> {selectedVendor.locationCity}, {selectedVendor.locationState}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Government Information</h4>
                  <div className="space-y-2">
                    <p><strong>Status:</strong> {getStatusBadge(selectedVendor.govStatus)}</p>
                    <p><strong>CAGE Code:</strong> {selectedVendor.cageCode || 'Not provided'}</p>
                    <p><strong>DUNS Number:</strong> {selectedVendor.dunsNumber || 'Not provided'}</p>
                    <p><strong>GSA Schedule:</strong> {selectedVendor.gsaScheduleId || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Certifications */}
              {selectedVendor.govCertifications && selectedVendor.govCertifications.length > 0 && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Government Certifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedVendor.govCertifications.map((cert, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded text-xs bg-emerald-100 text-emerald-700"
                      >
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Approved Agencies */}
              {selectedVendor.approvedAgencies && selectedVendor.approvedAgencies.length > 0 && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Approved Agencies</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedVendor.approvedAgencies.map((agency, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-700"
                      >
                        {agency}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents */}
              {selectedVendor.govDocsUrls && selectedVendor.govDocsUrls.length > 0 && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Government Documents</h4>
                  <div className="space-y-2">
                    {selectedVendor.govDocsUrls.map((doc, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <a
                          href={doc}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Document {index + 1}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {selectedVendor.govStatus === 'pending' && (
                <div className="flex space-x-4 pt-4 border-t">
                  <button
                    onClick={() => setShowApprovalModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Check className="w-4 h-4" />
                    <span>Approve</span>
                  </button>
                  <button
                    onClick={() => setShowRejectionModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <X className="w-4 h-4" />
                    <span>Reject</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedVendor && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Approve Government Certification</h3>
              <button
                onClick={() => setShowApprovalModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to approve government certification for <strong>{selectedVendor.companyName}</strong>?
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Add any notes about the approval..."
                />
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => handleApprove(selectedVendor.id)}
                  disabled={actionLoading === selectedVendor.id}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {actionLoading === selectedVendor.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  <span>Approve</span>
                </button>
                <button
                  onClick={() => setShowApprovalModal(false)}
                  disabled={actionLoading === selectedVendor.id}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedVendor && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Reject Government Certification</h3>
              <button
                onClick={() => setShowRejectionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Please provide a reason for rejecting <strong>{selectedVendor.companyName}</strong>'s government certification application.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <select
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Select a reason...</option>
                  <option value="Invalid CAGE Code">Invalid CAGE Code</option>
                  <option value="Invalid DUNS Number">Invalid DUNS Number</option>
                  <option value="Missing Required Certifications">Missing Required Certifications</option>
                  <option value="Insufficient Documentation">Insufficient Documentation</option>
                  <option value="Failed Verification">Failed Verification</option>
                  <option value="Ineligible Business Type">Ineligible Business Type</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Add any additional details about the rejection..."
                />
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => handleReject(selectedVendor.id)}
                  disabled={actionLoading === selectedVendor.id || !rejectionReason}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading === selectedVendor.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                  <span>Reject</span>
                </button>
                <button
                  onClick={() => setShowRejectionModal(false)}
                  disabled={actionLoading === selectedVendor.id}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 